import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  FileClock,
  Home,
  KeyRound,
  Lock,
  LogOut,
  Menu,
  Settings,
  SlidersHorizontal,
  User,
  UserCog,
  Wand2,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';

const licenseItems = [
  ['conf', 'Confluence'],
  ['jc', 'JIRA Core'],
  ['jira', 'JIRA Software'],
  ['jsm', 'Service Management'],
  ['crowd', 'Crowd'],
  ['crucible', 'Crucible'],
  ['bitbucket', 'Bitbucket'],
  ['bamboo', 'Bamboo'],
  ['plugin', '第三方插件'],
].map(([id, label]) => ({ id, label, path: `/licenses/${id}` }));

const userItems = [
  { label: '个人资料', description: '身份与组织信息', path: '/user/profile', icon: User },
  { label: '账号设置', description: '通知与界面偏好', path: '/user/settings', icon: Settings },
  { label: '修改密码', description: '更新登录凭据', path: '/change-password', icon: Lock },
];

const getMenuKeyForPath = (pathname) => {
  if (pathname.startsWith('/licenses/')) return 'licenses';
  if (pathname.startsWith('/user/') || pathname === '/change-password') return 'account';
  return null;
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(() => getMenuKeyForPath(location.pathname));
  const [mobileExpanded, setMobileExpanded] = useState({
    licenses: location.pathname.startsWith('/licenses/'),
    account: location.pathname.startsWith('/user/') || location.pathname === '/change-password',
  });

  const isAdmin = user?.role === 'admin' || user?.isAdmin;
  const initials = (user?.username || 'U').slice(0, 2).toUpperCase();
  const currentSection = useMemo(() => {
    if (location.pathname === '/') return '运行概览';
    if (location.pathname === '/generate') return '许可证生成';
    if (location.pathname.startsWith('/licenses/')) return '许可证档案';
    if (location.pathname.startsWith('/admin/')) return '系统配置';
    return '账户中心';
  }, [location.pathname]);

  useEffect(() => {
    setMobileOpen(false);
    const routeMenu = getMenuKeyForPath(location.pathname);
    setActiveMenu(routeMenu);
    if (routeMenu) {
      setMobileExpanded((value) => ({ ...value, [routeMenu]: true }));
    }
  }, [location.pathname]);

  useEffect(() => {
    const closeWithEscape = (event) => {
      if (event.key === 'Escape') {
        setActiveMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', closeWithEscape);
    return () => {
      document.removeEventListener('keydown', closeWithEscape);
    };
  }, []);

  const isActive = (path) => location.pathname === path;
  const go = (path) => navigate(path);
  const isGroupActive = (items) => items.some((item) => isActive(item.path));

  const handleLogout = () => {
    logout();
    navigate('/login?logout=true', { replace: true });
  };

  const renderDesktopMenu = (key, label, Icon, items) => (
    <div className="global-nav__group">
      <button
        type="button"
        className={cn('global-nav__trigger', isGroupActive(items) && 'is-active', activeMenu === key && 'is-open')}
        onClick={() => setActiveMenu(activeMenu === key ? null : key)}
        aria-expanded={activeMenu === key}
      >
        <Icon aria-hidden="true" /><span>{label}</span><ChevronDown aria-hidden="true" />
      </button>
      {activeMenu === key && (
        <div className="global-nav__panel global-nav__panel--inline">
          <div className="global-nav__panel-heading">
            <span>{key === 'licenses' ? '许可证资料库' : key === 'admin' ? '系统管理' : '账户与偏好'}</span>
            <strong>{label}</strong>
          </div>
          <div className={cn('global-nav__panel-grid', key === 'licenses' && 'global-nav__panel-grid--products')}>
            {items.map(({ label: itemLabel, description, path, icon: Icon }) => (
              <button
                type="button"
                key={path}
                className={cn('global-nav__panel-link', isActive(path) && 'is-active')}
                onClick={() => go(path)}
              >
                <span className="global-nav__panel-icon" aria-hidden="true">
                  {Icon ? <Icon /> : <KeyRound />}
                </span>
                <span><strong>{itemLabel}</strong>{description && <small>{description}</small>}</span>
                <ChevronRight aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderMobileGroup = (key, label, Icon, items) => (
    <div className="mobile-nav__group">
      <button
        type="button"
        className={cn('mobile-nav__group-trigger', mobileExpanded[key] && 'is-open')}
        onClick={() => setMobileExpanded((value) => ({ ...value, [key]: !value[key] }))}
        aria-expanded={mobileExpanded[key]}
      >
        <Icon aria-hidden="true" /><span>{label}</span><ChevronDown aria-hidden="true" />
      </button>
      <div className={cn('mobile-nav__group-content', mobileExpanded[key] && 'is-open')}>
        {items.map(({ label: itemLabel, path }) => (
          <button type="button" key={path} className={cn('mobile-nav__sub-link', isActive(path) && 'is-active')} onClick={() => go(path)}>
            <span>{itemLabel}</span><ChevronRight aria-hidden="true" />
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <header className="global-header">
        <div className="global-header__inner">
          <button type="button" className="global-brand" onClick={() => go('/')} aria-label="返回运行概览">
            <span className="global-brand__mark" aria-hidden="true"><KeyRound /></span>
            <span className="global-brand__copy"><strong>License Manager</strong><small>授权管理中心</small></span>
          </button>

          <nav className="global-nav" aria-label="全局导航">
            <span className="global-nav__label">工作区</span>
            <button type="button" className={cn('global-nav__link', isActive('/') && 'is-active')} onClick={() => go('/')}>
              <Home aria-hidden="true" /><span>概览</span>
            </button>
            <button type="button" className={cn('global-nav__link', isActive('/generate') && 'is-active')} onClick={() => go('/generate')}>
              <Wand2 aria-hidden="true" /><span>生成许可证</span>
            </button>
            <span className="global-nav__label">资源与账户</span>
            {renderDesktopMenu('licenses', '许可证档案', FileClock, licenseItems)}
            {renderDesktopMenu('account', '账户设置', UserCog, userItems)}
            {isAdmin && <span className="global-nav__label">管理</span>}
            {isAdmin && (
              <button
                type="button"
                className={cn('global-nav__link', isActive('/admin/configuration') && 'is-active')}
                onClick={() => go('/admin/configuration')}
              >
                <SlidersHorizontal aria-hidden="true" /><span>系统配置</span>
              </button>
            )}
          </nav>

          <div className="global-header__tools">
            <div className="runtime-pill runtime-pill--requirement" title="授权引擎最低运行要求">
              <span className="runtime-pill__requirement-icon" aria-hidden="true"><KeyRound /></span>
              <span><small>Agent Runtime</small><strong>Java 11.0.17+</strong></span>
            </div>
            <div className="header-operator">
              <span className="header-operator__avatar">{initials}</span>
              <span><strong>{user?.username || 'Operator'}</strong><small>{isAdmin ? '系统管理员' : '授权操作员'}</small></span>
            </div>
            <button type="button" className="header-logout" onClick={handleLogout} aria-label="退出登录" title="退出登录">
              <LogOut aria-hidden="true" />
            </button>
          </div>

          <div className="mobile-header__title">
            <small>ALM WORKSPACE</small>
            <strong>{currentSection}</strong>
          </div>
          <button type="button" className="mobile-menu-button" onClick={() => setMobileOpen(true)} aria-label="打开导航">
            <Menu aria-hidden="true" />
          </button>
        </div>
      </header>

      {mobileOpen && <button type="button" className="mobile-nav-backdrop" onClick={() => setMobileOpen(false)} aria-label="关闭导航" />}
      <aside className={cn('mobile-nav-sheet', mobileOpen && 'is-open')} aria-label="移动端导航" aria-hidden={!mobileOpen}>
        <div className="mobile-nav__header">
          <div className="global-brand">
            <span className="global-brand__mark" aria-hidden="true"><KeyRound /></span>
            <span className="global-brand__copy"><strong>License Manager</strong><small>授权管理中心</small></span>
          </div>
          <button type="button" className="mobile-nav__close" onClick={() => setMobileOpen(false)} aria-label="关闭导航"><X /></button>
        </div>

        <div className="mobile-nav__runtime">
          <span className="runtime-pill__requirement-icon" aria-hidden="true"><KeyRound /></span>
          <div><small>AGENT RUNTIME</small><strong>Java 11.0.17+</strong></div>
          <code>REQUIRED</code>
        </div>

        <nav className="mobile-nav__content">
          <span className="mobile-nav__label">WORKSPACE</span>
          <button type="button" className={cn('mobile-nav__link', isActive('/') && 'is-active')} onClick={() => go('/')}><Home /><span>运行概览</span><ChevronRight /></button>
          <button type="button" className={cn('mobile-nav__link', isActive('/generate') && 'is-active')} onClick={() => go('/generate')}><Wand2 /><span>生成许可证</span><ChevronRight /></button>
          <span className="mobile-nav__label">RESOURCES</span>
          {renderMobileGroup('licenses', '许可证档案', FileClock, licenseItems)}
          {renderMobileGroup('account', '账户中心', UserCog, userItems)}
          {isAdmin && (
            <>
              <span className="mobile-nav__label">SYSTEM</span>
              <button
                type="button"
                className={cn('mobile-nav__link', isActive('/admin/configuration') && 'is-active')}
                onClick={() => go('/admin/configuration')}
              >
                <SlidersHorizontal /><span>系统配置</span><ChevronRight />
              </button>
            </>
          )}
        </nav>

        <div className="mobile-nav__footer">
          <div className="header-operator"><span className="header-operator__avatar">{initials}</span><span><strong>{user?.username || 'Operator'}</strong><small>{isAdmin ? '系统管理员' : '授权操作员'}</small></span></div>
          <button type="button" className="mobile-nav__logout" onClick={handleLogout}><LogOut />退出登录</button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
